import { differenceInMinutes, format, isSameYear } from 'date-fns'

import type { ConversationCardData } from '../fragments/conversationCard'

type Participant = ConversationCardData['participants'][number]
type Preview = NonNullable<ConversationCardData['preview']>

export type Counterpart = {
  /** Who the conversation is with, as the row should print it. */
  name: string
  /** Handle of the first counterpart, without the network's prefix. */
  username: string | null
  /** Avatar of the first counterpart; a group shows one face, not a stack. */
  avatar: string
  /** Seed for the letter fallback when the avatar does not load. */
  fallbackName: string
}

/**
 * The people a conversation is with: every participant except our own channel.
 *
 * Someone who left a group stays in `participants` with `isActive: false`.
 * They are dropped from the name so the row reads as who is still in the
 * chat, unless nobody is left, in which case the last members are shown
 * rather than an empty name.
 */
export function getCounterpart(
  participants: ReadonlyArray<Participant>,
): Counterpart {
  const others = participants.filter((participant) => !participant.isOwner)
  const active = others.filter((participant) => participant.isActive)
  const shown = active.length > 0 ? active : others

  if (shown.length === 0) {
    return {
      name: 'Unknown',
      username: null,
      avatar: '',
      fallbackName: 'Unknown',
    }
  }

  const first = shown[0].author
  return {
    name: shown
      .map((participant) => displayName(participant.author))
      .join(', '),
    username: first.username ?? null,
    avatar: first.avatar,
    fallbackName: displayName(first),
  }
}

function displayName(author: Participant['author']): string {
  return author.name ?? author.username ?? 'Unknown'
}

/**
 * How the row reads a conversation: the wire status, which already names the
 * three states (unreplied, replied, resolved).
 *
 * A status outside those three, such as a value the API adds after this
 * client was built, falls back to who wrote the newest message, which is what
 * the API derives `replied` and `unreplied` from.
 */
export type ConversationState = 'unreplied' | 'replied' | 'resolved'

const CONVERSATION_STATES: ReadonlySet<string> = new Set<ConversationState>([
  'unreplied',
  'replied',
  'resolved',
])

function isConversationState(status: string): status is ConversationState {
  return CONVERSATION_STATES.has(status)
}

export function getConversationState(
  conversation: Pick<ConversationCardData, 'status' | 'preview'>,
): ConversationState {
  if (isConversationState(conversation.status)) return conversation.status
  return conversation.preview?.isOwnMessage ? 'replied' : 'unreplied'
}

export type PreviewLine = {
  text: string
  /**
   * `new` is the brand-coloured "[n] new messages" line and `failed` the red
   * "Message not sent" line; the others are the message preview in the
   * weight and colour the state calls for.
   */
  tone: 'new' | 'unreplied' | 'replied' | 'resolved' | 'failed'
}

/**
 * The row's second line. An unanswered conversation with several new
 * messages says how many instead of quoting the last one. A reply of ours the network
 * rejected says so instead of quoting it, so the failure is visible from the
 * list. Otherwise it is the newest message: its text, or a description of
 * non-text content, prefixed with "You:" when we sent it. A reply still on
 * its way reads like a sent one.
 */
export function getPreviewLine(
  conversation: Pick<ConversationCardData, 'status' | 'preview' | 'readState'>,
): PreviewLine {
  const state = getConversationState(conversation)

  if (state === 'unreplied' && conversation.readState.unreadCount > 1) {
    return {
      text: `${conversation.readState.unreadCount} new messages`,
      tone: 'new',
    }
  }

  const preview = conversation.preview
  if (preview?.isOwnMessage && isFailedSendState(preview.send?.state)) {
    return { text: 'Message not sent', tone: 'failed' }
  }

  return { text: describePreview(preview), tone: state }
}

/**
 * Whether the network rejected our send, shared by the list and the thread.
 */
export function isFailedSendState(
  state: NonNullable<Preview['send']>['state'] | null | undefined,
): boolean {
  return state === 'error'
}

/**
 * The newest message as one line. A text message with no body, which the
 * network can send, falls through to the generic label so an own message
 * never renders as a bare "You:".
 */
function describePreview(preview: Preview | null | undefined): string {
  if (!preview) return ''
  const body =
    preview.text || describeContent(preview.contentType) || 'Sent a message'
  return preview.isOwnMessage ? `You: ${body}` : body
}

/**
 * A one-line stand-in for content that is not text, shared by the list's
 * preview and the thread's placeholder bubbles. Empty for text, so callers
 * can fall through to the message body.
 */
export function describeContent(contentType: Preview['contentType']): string {
  switch (contentType) {
    case 'image':
      return 'Sent a photo'
    case 'video':
      return 'Sent a video'
    case 'audio':
      return 'Sent a voice message'
    case 'gif':
      return 'Sent a GIF'
    case 'sticker':
      return 'Sent a sticker'
    case 'file':
      return 'Sent a file'
    case 'carousel':
      return 'Sent an album'
    case 'share':
      return 'Shared a post'
    case 'story':
      return 'Replied to a story'
    case 'text':
      return ''
    case 'unsupported':
    default:
      return 'Sent a message'
  }
}

/**
 * Compact age for the row's trailing corner: "now", "12m", "3h", "5d", then a
 * calendar date once it is older than a week. Compact because the column is
 * narrow and the exact time belongs to the thread, not the list.
 */
export function formatShortTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  const minutes = Math.max(0, differenceInMinutes(now, date))

  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`

  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`

  return isSameYear(date, now)
    ? format(date, 'MMM d')
    : format(date, 'MMM d, yyyy')
}
